import React from 'react';
import { LightbulbIcon, CheckCircleIcon } from 'lucide-react';
interface SolutionProps {
  stickyButtonRef?: React.RefObject<HTMLHeadingElement>;
}
export const Solution: React.FC<SolutionProps> = ({
  stickyButtonRef
}) => {
  return <section className="mb-10" data-id="element-389">
      <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-390">
        Twee werelden en twee talen - de barrière die gedragsproblemen bij
        honden veroorzaakt:
      </h2>

      <p className="mb-6 leading-relaxed" data-id="element-391">
        Wij mensen zijn pratende wezens. We communiceren met woorden, stemtonen
        en handgebaren. Als we blij zijn, zeggen we dat. Als we iets willen,
        vragen we erom.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-392">
        Honden? Die communiceren totaal anders.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-393">
        Hun taal is subtiel, stil en bijna onzichtbaar voor het ongetrainde oog.
      </p>

      <ul className="mb-6 space-y-2 ml-6" data-id="element-394">
        <li className="leading-relaxed" data-id="element-395">
          • Een lichte draaiing van het hoofd.
        </li>
        <li className="leading-relaxed" data-id="element-396">• Een korte blik opzij.</li>
        <li className="leading-relaxed" data-id="element-397">
          • De manier waarop ze hun oren houden.
        </li>
        <li className="leading-relaxed" data-id="element-398">• De positie van hun staart.</li>
      </ul>

      <p className="mb-6 leading-relaxed" data-id="element-399">
        Dit is hondentaal. En wij mensen spreken het van nature niet.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-400">
        Dat betekent dat jij — met de beste intenties — dingen tegen je hond
        'zegt' die een andere betekenis hebben dan jouw bedoeling.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-401">
        En het betekent dat je hond jou dingen probeert te vertellen die je
        simpelweg niet opvangt.
      </p>

      <p className="mb-6 leading-relaxed font-semibold" data-id="element-402">
        Laat me je een simpel praktijkvoorbeeld geven:
      </p>

      <div className="bg-blue-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500" data-id="element-403">
        <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-404">
          Wat je hond ziet wanneer jij hem troost
        </h3>

        <p className="mb-4 leading-relaxed" data-id="element-405">
          Je hond is onrustig en misschien wat angstig.
        </p>

        <p className="mb-4 leading-relaxed" data-id="element-406">
          Dus: jij buigt voorover, aait hem over zijn kop, en zegt met een lieve
          stem: "Rustig maar, het is oké."
        </p>

        <p className="mb-4 leading-relaxed" data-id="element-407">
          In mensentaal is dit troost, geruststelling en liefde.
        </p>

        <p className="mb-4 leading-relaxed" data-id="element-408">Maar in hondentaal?</p>

        <ul className="mb-4 space-y-2 ml-6" data-id="element-409">
          <li className="leading-relaxed" data-id="element-410">
            → Voorover buigen is confronterend
          </li>
          <li className="leading-relaxed" data-id="element-411">
            → Over de kop aaien kan als dominant worden ervaren
          </li>
          <li className="leading-relaxed" data-id="element-412">
            → En je hoge, lieve stem? Die klinkt voor je hond als opwinding —
            alsof er inderdaad iets aan de hand is
          </li>
        </ul>

        <p className="font-semibold text-blue-800" data-id="element-413">
          Jouw bedoeling: "Maak je geen zorgen."
          <br data-id="element-414" />
          Wat je hond ontvangt: "Er is inderdaad iets mis."
        </p>
      </div>

      <p className="mb-6 leading-relaxed" data-id="element-415">
        En zo wordt je troost onbedoeld een bevestiging van zijn angst. Het
        lijkt onschuldig, maar…
      </p>

      <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-416">
        Wat de taalbarrière tussen jou en je hond met jullie band doet:
      </h2>

      <p className="mb-6 leading-relaxed" data-id="element-417">
        Het voorbeeld hierboven over troosten is natuurlijk onschuldig. Als het
        één keer op die manier gebeurt, is er niets aan de hand.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-418">Maar het punt is:</p>

      <p className="mb-6 leading-relaxed" data-id="element-419">
        Op één dag heb je tientallen interacties met je hond…
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-420">
        Je hond probeert je constant iets te vertellen. Met zijn ogen,
        lichaamshouding en bewegingen.
      </p>

      <p className="mb-4 leading-relaxed" data-id="element-421">Denk aan:</p>

      <ul className="mb-6 space-y-2 ml-6" data-id="element-422">
        <li className="leading-relaxed italic" data-id="element-423">"Ik ben gestrest."</li>
        <li className="leading-relaxed italic" data-id="element-424">"Ik voel me niet veilig."</li>
        <li className="leading-relaxed italic" data-id="element-425">"Ik heb even ruimte nodig."</li>
        <li className="leading-relaxed italic" data-id="element-426">"Kun je me helpen?"</li>
      </ul>

      <p className="mb-6 leading-relaxed" data-id="element-427">
        Maar als je zijn taal niet spreekt, vang je deze boodschappen niet op.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-428">
        Dan zie je alleen het eindresultaat: het angstige gedrag. Het blaffen,
        de onrust of spanning.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-429">
        En je hond? Die voelt zich niet begrepen.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-430">
        Niet omdat je niet van hem houdt, maar omdat hij het je niet kan
        vertellen op een manier die jij begrijpt.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-431">
        Het resultaat? Je blijft raden in plaats van weten. Je probeert dingen
        die niet werken. En die diepe vertrouwensband die je zoekt — die 'once
        in a lifetime' connectie — blijft net buiten bereik.
      </p>

      <div className="w-full mb-6" data-id="element-432">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wMD9H36KPsrVSLkgoCNxr9/Scherm_afbeelding_2025-08-15_om_15.37.25-min.png" alt="Vrouw communiceert met haar hond in een park" className="w-full h-auto rounded-lg shadow-md" data-id="element-433" />
        <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-434">
          Wat als je je hond wél kon verstaan?
        </p>
      </div>

      <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-435">
        Stel je even voor: Wat als je je hond wél kon verstaan?
      </h2>

      <p className="mb-6 leading-relaxed" data-id="element-436">
        Je kijkt naar je hond en je ziet niet langer 'vreemd gedrag' of
        'problemen'...
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-437">
        Je ziet communicatie. Duidelijke, heldere communicatie.
      </p>

      <p className="mb-4 leading-relaxed font-semibold" data-id="element-438">3 voorbeelden:</p>

      <ul className="mb-6 space-y-3" data-id="element-439">
        <li className="flex items-start" data-id="element-440">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-441" />
          <span data-id="element-442">
            Die korte blik naar de deur? Je weet precies wat hij bedoelt.
          </span>
        </li>
        <li className="flex items-start" data-id="element-443">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-444" />
          <span data-id="element-445">
            Dat wegkijken wanneer je hem aankijkt? Het is geen negeren — het is
            respect. En nu kun je dat respect beantwoorden.
          </span>
        </li>
        <li className="flex items-start" data-id="element-446">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-447" />
          <span data-id="element-448">
            Die onrust 's avonds? Je herkent de signalen voordat het escaleert.
            En je weet precies hoe je hem gerust kunt stellen — op een manier
            die hij daadwerkelijk begrijpt.
          </span>
        </li>
      </ul>

      <p className="mb-6 leading-relaxed" data-id="element-449">
        Dan zitten jullie écht op één lijn. Stel je eens voor:
      </p>

      <ul className="mb-6 space-y-3" data-id="element-450">
        <li className="flex items-start" data-id="element-451">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-452" />
          <span data-id="element-453">
            Dat je precies weet wat je hond nodig heeft. Niet door te gokken,
            maar door te kijken naar je hond.
          </span>
        </li>
        <li className="flex items-start" data-id="element-454">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-455" />
          <span data-id="element-456">Dat wandelingen ontspannen zijn — voor jullie allebei.</span>
        </li>
        <li className="flex items-start" data-id="element-457">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-458" />
          <span data-id="element-459">
            Dat je hond je aankijkt met die blik van: "Jij begrijpt me."
          </span>
        </li>
      </ul>

      <p className="mb-6 leading-relaxed font-semibold text-blue-800 text-lg" data-id="element-460">
        Dát is wat er mogelijk is wanneer je de taalbarrière doorbreekt.
      </p>

      <h2 ref={stickyButtonRef} className="text-xl font-bold text-blue-800 mb-4" data-id="element-461">
        De sleutel tot een goede band met je hond is: Liefdevol Communiceren
      </h2>

      <p className="mb-6 leading-relaxed" data-id="element-462">
        Dit is niet nog een trainingsmethode die je leert hoe je je hond moet
        veranderen.
      </p>

      <p className="mb-6 leading-relaxed font-semibold text-blue-800" data-id="element-463">
        Dit is zijn taal leren spreken.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-464">
        <a href="https://stresslessdogs.nl/lc-fb" className="bg-yellow-200 hover:bg-yellow-300 text-blue-800 font-semibold px-1 rounded transition-colors cursor-pointer" target="_blank" rel="noopener noreferrer" data-id="element-465">
          In de cursus 'Liefdevol Communiceren'
        </a>{' '}
        leer je precies hoe honden onderling communiceren. Dezelfde signalen die
        honden al duizenden jaren gebruiken om elkaar te begrijpen.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-466">
        En het mooie is: zodra je deze taal begrijpt, kun je hem ook zelf
        gebruiken. Dan 'praat' je terug op een manier die je hond direct
        begrijpt.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-467">
        Geen dwang. Geen correcties. Geen straffen.
      </p>

      <p className="mb-6 leading-relaxed" data-id="element-468">
        Want dat is het fundamentele verschil:
      </p>

      <div className="bg-blue-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500" data-id="element-469">
        <p className="mb-4 leading-relaxed" data-id="element-470">
          Traditionele training draait om gehoorzaamheid. Om je hond te laten
          doen wat jij wilt.
        </p>
        <p className="font-semibold text-blue-800" data-id="element-471">
          Liefdevol Communiceren draait om begrip. Om samenwerking. Om een
          relatie waarin jullie elkaar begrijpen.
        </p>
      </div>

      <p className="mb-6 leading-relaxed" data-id="element-472">
        Wanneer je hond je begrijpt — en zich door jou begrepen voelt —
        verandert alles.
      </p>

      <ul className="mb-6 space-y-3" data-id="element-473">
        <li className="flex items-start" data-id="element-474">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-475" />
          <span data-id="element-476">
            Hij wordt rustiger. Niet omdat hij gecorrigeerd wordt, maar omdat
            hij zich veilig voelt.
          </span>
        </li>
        <li className="flex items-start" data-id="element-477">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-478" />
          <span data-id="element-479">Hij luistert beter. Niet uit angst, maar uit vertrouwen.</span>
        </li>
        <li className="flex items-start" data-id="element-480">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-481" />
          <span data-id="element-482">
            En de band? Die wordt dieper dan je ooit voor mogelijk hield.
          </span>
        </li>
      </ul>
    </section>;
};